import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "api/axiosConfig";
import "assets/css/UploadForm.css";
import UploadTemplateForm from "./UploadTemplateForm";
import { readFileContent } from "./utils/fileHelpers";
import { validateField } from "./utils/validation";
import { checkPlaceholders } from "./utils/placeholderHelpers";
import SuccessModal from "components/Common/SuccessModal";

const initialState = {
  name: "",
  subject: "",
  type: "email_template",
  base_html: null,
  placeholders_json: null,
  target_languages: [],
  hasConsented: false,
};

const UploadNewForm = () => {
  const [formData, setFormData] = useState(initialState);
  const [languages, setLanguages] = useState([]);
  const [errors, setErrors] = useState({});
  const [jsonError, setJsonError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidationAttempted, setIsValidationAttempted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const baseHtmlFileRef = useRef(null);
  const placeholdersJsonFileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await apiClient.get("languages");
        const options = response.data.data.map(({ code, name }) => ({ value: code, label: name }));
        setLanguages(options);
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    })();
  }, []);

  const validateFieldCallback = useCallback(
    (fieldName, value) => validateField(fieldName, value, formData.type),
    [formData.type]
  );

  const validateAllFields = useCallback(() => {
    const newErrors = Object.keys(formData).reduce((acc, key) => {
      const error = validateFieldCallback(key, formData[key]);
      if (error) acc[key] = error;
      return acc;
    }, {});
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateFieldCallback]);

  const handleChange = useCallback(
    (e) => {
      const { name, type, files, value, checked } = e.target;
      const newValue = type === "file" ? files[0] : type === "checkbox" ? checked : value;
      setFormData((prev) => ({ ...prev, [name]: newValue }));

      if (name === "base_html" || name === "placeholders_json") {
        setJsonError("");
        setErrors((prev) => ({ ...prev, placeholders_json: undefined }));
      }

      if (isValidationAttempted) {
        const error = validateFieldCallback(name, newValue);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [isValidationAttempted, validateFieldCallback]
  );

  const handleLanguageChange = useCallback(
    (selectedOptions) => {
      const selectedValues = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
      setFormData((prev) => ({ ...prev, target_languages: selectedValues }));

      if (isValidationAttempted) {
        const error = validateFieldCallback("target_languages", selectedValues);
        setErrors((prev) => ({ ...prev, target_languages: error }));
      }
    },
    [isValidationAttempted, validateFieldCallback]
  );

  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
    setJsonError("");
    setIsValidationAttempted(false);
    
    if (baseHtmlFileRef.current) {
      baseHtmlFileRef.current.value = "";
    }
    if (placeholdersJsonFileRef.current) {
      placeholdersJsonFileRef.current.value = "";
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsValidationAttempted(true);

    if (!validateAllFields()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const [htmlContent, jsonContent] = await Promise.all([
        readFileContent(formData.base_html),
        readFileContent(formData.placeholders_json),
      ]);
      const json = JSON.parse(jsonContent);
      const missingKey = checkPlaceholders(htmlContent, json);

      if (missingKey) {
        setErrors((prev) => ({
          ...prev,
          placeholders_json: `Missing placeholder: {{${missingKey}}} in HTML. Please re-upload corrected files.`,
        }));
        setIsSubmitting(false);
        return;
      }

      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "target_languages") {
          payload.append(key, value.join(","));
        } else if (key === "hasConsented") {
          payload.append(key, value);
        } else if (value) {
          payload.append(key, value);
        }
      });

      const response = await apiClient.post("/templates", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const message = response?.data?.message || "Base Template created successfully!";
      setSuccessMessage(message);
      setShowSuccessModal(true);
      resetForm();
    } catch (error) {
      console.error(error);
      const backendErrors = error.response?.data?.errors;
      const placeholderError = error.response?.data?.placeholders_json;

      if (typeof backendErrors === "string") {
        setJsonError(backendErrors);
      } else if (backendErrors && typeof backendErrors === "object") {
        setErrors(
          Object.fromEntries(
            Object.entries(backendErrors).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value])
          )
        );
      } else if (placeholderError) {
        setJsonError(placeholderError);
      } else {
        setJsonError("An error occurred during submission.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      <UploadTemplateForm
        formData={formData}
        languages={languages}
        errors={errors}
        jsonError={jsonError}
        isSubmitting={isSubmitting}
        handleChange={handleChange}
        handleLanguageChange={handleLanguageChange}
        handleSubmit={handleSubmit}
        baseHtmlFileRef={baseHtmlFileRef}
        placeholdersJsonFileRef={placeholdersJsonFileRef}
      />

      <SuccessModal show={showSuccessModal} onClose={handleCloseSuccessModal} message={successMessage} />
    </>
  );
};

export default UploadNewForm;